import { AdminOrganizations } from '@platform/client';
import { Button, FormsCheckBox, FormsMultilineInput, InlineFormInput } from '@platform/components';
import { BusinessChallengesEnum } from '@platform/client/schemas';
import { useQueryClient } from '@tanstack/react-query';
import { useContext, useEffect } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { BusinessChallengeSelectionDropdown } from '../../../components/BusinessChallengeSelectionDropdown';
import { CauseSelectionDropdown } from '../../../components/CauseSelectionDropdown';
import { CorporateClientSelectionDropdown } from '../../../components/CorporateClientSelectionDropdown';
import { Divider } from '../../../components/Divider';
import { ImpactPartnerSelectionDropdown } from '../../../components/ImpactPartnerSelectionDropdown';
import { IndustrySelectionDropdown } from '../../../components/IndustrySelectionDropdown';
import { MultipleCountrySelectionDropdown } from '../../../components/MultipleCountrySelectionDropdown';
import { OrganizationSizeSelectionDropdown } from '../../../components/OrganizationSIzeSelectionDropdown';
import { OrgTypeSelectionDropdown } from '../../../components/OrgTypeSelectionDropdown';
import { SingleCountrySelectionDropdown } from '../../../components/SingleCountrySelectionDropdown';

import { AdminTableContext } from '../../AdminTable';
import { useParams } from 'wouter';
import { navigate } from 'wouter/use-browser-location';

type UpdateStatusFormData = {
  name?: string;
  description?: string;
  impactStory?: string;
  businessProblem?: string;
  businessSolution?: string;

  firstBusinessChallenge?: BusinessChallengesEnum;
  secondBusinessChallenge?: BusinessChallengesEnum;
  thirdBusinessChallenge?: BusinessChallengesEnum;

  firstBusinessChallengeDescription?: string | null;
  secondBusinessChallengeDescription?: string | null;
  thirdBusinessChallengeDescription?: string | null;

  website?: string;
  impactPartners?: string[];
  industries?: string[];
  countries?: string[];
  hqCountry?: string;
  causes?: string[];
  orgSize?: string;
  orgType?: string;
  registrationYear?: number;

  linkedin?: string;

  generatingRevenue?: boolean;
  corporateClient?: string;
};

export const OrganizationEdit = () => {
  const { organizationId } = useParams<{ organizationId: string }>();
  const { pagination } = useContext(AdminTableContext);

  const organization = AdminOrganizations.useGetReviewData(parseInt(organizationId)).data;
  const validateChallengesWithContext = (
    value?: BusinessChallengesEnum,
    context?: string | null,
  ) => {
    if ((value && context) || (!value && !context)) {
      return true;
    } else {
      return 'Please provide a selection and context for this business challenge';
    }
  };

  const { control, handleSubmit, watch, formState, register, setValue } =
    useForm<UpdateStatusFormData>({
      mode: 'onBlur',
      reValidateMode: 'onChange',
    });

  useEffect(() => {
    if (organization) {
      setValue('name', organization.name || '');
      setValue('website', organization.website || '');
      setValue('linkedin', organization.linkedin || '');
      setValue('description', organization.description || '');
      setValue('businessProblem', organization.businessProblem || '');
      setValue('businessSolution', organization.businessSolution || '');
      setValue('impactStory', organization.impactStory || '');
      setValue(
        'firstBusinessChallenge',
        (organization.challengeOne as BusinessChallengesEnum) || null,
      );
      setValue(
        'secondBusinessChallenge',
        (organization.challengeTwo as BusinessChallengesEnum) || null,
      );
      setValue(
        'thirdBusinessChallenge',
        (organization.challengeThree as BusinessChallengesEnum) || null,
      );
      setValue('firstBusinessChallengeDescription', organization.contextOne || null);
      setValue('secondBusinessChallengeDescription', organization.contextTwo || null);
      setValue('thirdBusinessChallengeDescription', organization.contextThree || null);
      setValue('impactPartners', organization.partners || []);
      setValue('industries', organization.industries || []);
      setValue('countries', organization.countries || []);
      setValue('causes', organization.causes || []);
      setValue('generatingRevenue', organization.generatingRevenue || false);
      setValue('hqCountry', organization.hqCountryId?.toString() || '');
      setValue('orgSize', organization.organizationSizeId?.toString() || '');
      setValue('orgType', organization.organizationTypeId?.toString() || '');
      setValue('registrationYear', organization.registrationDate || undefined);
      setValue('corporateClient', organization.corporateClient || '');
    }
  }, [organization, setValue]);

  const errors = formState.errors;

  const queryClient = useQueryClient();

  const updateMutator = AdminOrganizations.useUpdateOrganization({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [
            ...AdminOrganizations.getGetOrganizationsQueryKey({
              page: pagination.pageIndex,
              limit: pagination.pageSize,
            }),
          ],
        });
        queryClient.invalidateQueries({
          queryKey: [...AdminOrganizations.getGetReviewDataQueryKey(parseInt(organizationId))],
        });
        navigate(`/platform/admin/organizations/${organizationId}`);
      },
    },
  });

  const onSubmit: SubmitHandler<UpdateStatusFormData> = (data) => {
    updateMutator.mutate({
      data: {
        organizationId: parseInt(organizationId),
        name: data.name ?? null,
        website: data.website ?? null,
        linkedin: data.linkedin ?? null,
        description: data.description ?? null,
        impactPartners: data.impactPartners ?? null,
        industries: data.industries ?? null,
        countries: data.countries ?? null,
        causes: data.causes ?? null,
        generatingRevenue: data.generatingRevenue ?? null,
        challengeOneDescription: data.firstBusinessChallengeDescription ?? null,
        challengeTwoDescription: data.secondBusinessChallengeDescription ?? null,
        challengeThreeDescription: data.thirdBusinessChallengeDescription ?? null,
        challengeOne: data.firstBusinessChallenge ?? null,
        challengeTwo: data.secondBusinessChallenge ?? null,
        challengeThree: data.thirdBusinessChallenge ?? null,
        impactStory: data.impactStory ?? null,
        corporateClient: data.corporateClient ?? null,
        organizationType: data.orgType ?? null,
        organizationSize: data.orgSize ?? null,
        registrationYear: data.registrationYear ?? null,
        businessModel: null,
        businessModelDescription: null,
        businessProblem: data.businessProblem ?? null,
        businessSolution: data.businessSolution ?? null,
        registrationCountry: data.hqCountry ?? null,
        logoUrl: null,
      },
    });
  };

  const onImpactPartnersSelectionChange = (selectedImpactPartners: string[]) => {
    setValue('impactPartners', selectedImpactPartners);
  };

  const onIndustrySelection = (selectedIndustries: string[]) => {
    setValue('industries', selectedIndustries);
  };

  const onCountrySelection = (selectedCountries: string[]) => {
    setValue('countries', selectedCountries);
  };

  const onCauseSelection = (selectedCauses: string[]) => {
    setValue('causes', selectedCauses);
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className='flex flex-col gap-2 w-full p-4 md:w-[800px] mx-auto shadow-sm border border-gray-200 rounded-lg'
    >
      <InlineFormInput
        label='Organization name'
        placeholder="Enter organization's full name"
        register={() =>
          register('name', {
            required: 'Organization name is required',
          })
        }
        errors={errors.name}
      />
      <InlineFormInput
        label='Website'
        placeholder="Enter organization's website"
        register={() => register('website')}
        errors={errors.website}
      />
      <InlineFormInput
        label='LinkedIn URL'
        placeholder="Enter organization's LinkedIn URL"
        register={() => register('linkedin')}
        errors={errors.linkedin}
      />
      <FormsMultilineInput
        label='Description'
        control={control}
        name='description'
        formsError={formState.errors.description}
      />
      <FormsMultilineInput
        label='Business Problem'
        control={control}
        name='businessProblem'
        formsError={formState.errors.businessProblem}
      />
      <FormsMultilineInput
        label='Business Solution'
        control={control}
        name='businessSolution'
        formsError={formState.errors.businessSolution}
      />
      <FormsMultilineInput
        label='Impact Story'
        control={control}
        name='impactStory'
        formsError={formState.errors.impactStory}
      />
      <ImpactPartnerSelectionDropdown
        selectedImpactPartners={watch('impactPartners') || []}
        onImpactPartnersSelectionChange={onImpactPartnersSelectionChange}
        register={() => register('impactPartners')}
        label={'Impact Partners'}
      />
      <CorporateClientSelectionDropdown
        name='corporateClient'
        control={control}
        label={'Corporate Client'}
      />
      <div className='py-4'>
        <Divider />
      </div>
      <BusinessChallengeSelectionDropdown
        label='First Business Challenge'
        name='firstBusinessChallenge'
        control={control}
        rules={{
          validate: () =>
            validateChallengesWithContext(
              watch('firstBusinessChallenge'),
              watch('firstBusinessChallengeDescription'),
            ),
        }}
        error={formState.errors.firstBusinessChallenge}
      />
      <FormsMultilineInput
        label='Challenge one context'
        control={control}
        name='firstBusinessChallengeDescription'
        formsError={formState.errors.firstBusinessChallengeDescription}
      />
      <BusinessChallengeSelectionDropdown
        label='Second Business Challenge'
        name='secondBusinessChallenge'
        control={control}
        rules={{
          validate: () =>
            validateChallengesWithContext(
              watch('secondBusinessChallenge'),
              watch('secondBusinessChallengeDescription'),
            ),
        }}
        error={formState.errors.secondBusinessChallenge}
      />
      <FormsMultilineInput
        label='Challenge two context'
        control={control}
        name='secondBusinessChallengeDescription'
        formsError={formState.errors.secondBusinessChallengeDescription}
      />
      <BusinessChallengeSelectionDropdown
        label='Third Business Challenge'
        control={control}
        name='thirdBusinessChallenge'
        rules={{
          validate: () =>
            validateChallengesWithContext(
              watch('thirdBusinessChallenge'),
              watch('thirdBusinessChallengeDescription'),
            ),
        }}
        error={formState.errors.thirdBusinessChallenge}
      />
      <FormsMultilineInput
        label='Challenge three context'
        control={control}
        name='thirdBusinessChallengeDescription'
        formsError={formState.errors.thirdBusinessChallengeDescription}
      />
      <Divider />
      <IndustrySelectionDropdown
        selectedIndustries={watch('industries') || []}
        onIndustriesSelectionChange={onIndustrySelection}
        label='Industries'
      />
      <MultipleCountrySelectionDropdown
        selectedCountries={watch('countries') || []}
        onCountriesSelectionChange={onCountrySelection}
        label='Countries of operations'
      />
      <CauseSelectionDropdown
        selectedCauses={watch('causes') || []}
        onCausesSelectionChange={onCauseSelection}
        label='Causes (SDGs)'
      />
      <div className='px-2'>
        <FormsCheckBox
          size='sm'
          name='generatingRevenue'
          control={control}
          description='Generating revenue'
          formsError={errors.generatingRevenue}
        />
      </div>
      <SingleCountrySelectionDropdown
        control={control}
        label='Headquarters Location'
        name='hqCountry'
      />
      <OrganizationSizeSelectionDropdown
        control={control}
        label='Organization Size'
        name='orgSize'
      />
      <OrgTypeSelectionDropdown control={control} label='Organization Type' name='orgType' />
      <InlineFormInput
        label='Year your organization was registered'
        inputType='number'
        placeholder='Enter year your organization was registered'
        register={() => register('registrationYear')}
        errors={errors.registrationYear}
      />
      <div className='mt-2 w-full flex justify-end gap-3'>
        <Button size='sm' variant='secondary-gray' href={`/${organizationId}`}>
          Cancel
        </Button>
        <Button size='sm' variant='primary' type='submit'>
          Save
        </Button>
      </div>
    </form>
  );
};
